import { Component, Input, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { EMPTY, Observable } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { Client, ClientRepresentative, Invoice, Permission } from 'src/app/api/models';
import { ClientsService } from 'src/app/api/services';
import { UserService } from 'src/app/service/user.service';
import { InvoicePeerService } from '../../service/invoice-peer.service';
import { canonicalizeBusinessIdControl, clientBusinessIdValidator } from '../../validators/business-id/angular-adapter';

interface ClientAndRepresentative {
  client: Client,
  representative: ClientRepresentative,
}

@Component({
  selector: 'app-invoice-le-client-widget',
  templateUrl: './invoice-le-client-widget.component.html',
  styleUrls: ['./invoice-le-client-widget.component.scss']
})
export class InvoiceLeClientWidgetComponent implements OnInit {

  @Input()
  expanded: boolean;

  clientAndRepresentative$: Observable<ClientAndRepresentative>;

  form: UntypedFormGroup;

  /**
   * Whether the user may save the client, which the edit form's Save does
   * through PUT /clients/{id} (clients:edit, issue #678: a rename starts a
   * Verification of Payee). Unknown, not missing, until the current
   * user loads.
   */
  readonly canEditClient$: Observable<boolean>;
  readonly editPermissionTooltip = `Requires the ${Permission.ClientsEdit} permission`;

  edited = false;
  locked = false;

  constructor(
    private builder: UntypedFormBuilder,
    private invoicePeerService: InvoicePeerService,
    private clientsService: ClientsService,
    userService: UserService,
  ) {
    this.canEditClient$ = userService.hasPermission(Permission.ClientsEdit);
    this.form = this.builder.group({
      client: this.builder.group({
        invoicing_service_company_business_id: [null, [Validators.required, clientBusinessIdValidator()]],
      }),
      representative: this.builder.group({
        first_name: [null, Validators.required],
        last_name: [null, Validators.required],
        email: [null, Validators.email],
        phone: [null]
      })
    });
  }

  edit(client: Client, representative: ClientRepresentative): void {
    this.invoicePeerService.lock();
    this.edited = true;
    this.form.reset();
    this.form.patchValue({
      client: client,
      representative: representative,
    });
    // business_id is not operator-editable on an existing client: disabling it
    // keeps a legacy non-conforming id from invalidating the form and lets the
    // canonicalize helper skip it (design D10).
    this.form.get('client.invoicing_service_company_business_id').disable();
  }

  revert(): void {
    this.edited = false;
    this.invoicePeerService.unlock();
    this.form.reset();
  }

  save(client: Client, representative: ClientRepresentative): void {
    // business_id is disabled in edit mode (a stored value), so the helper skips
    // it and getRawValue() submits it byte-identical (design D10).
    canonicalizeBusinessIdControl(this.form.get('client.invoicing_service_company_business_id'));

    Object.assign(client, this.form.get('client').getRawValue());
    Object.assign(representative, this.form.value.representative);

    this.clientsService.updateClient({ id: client.id, body: client }).subscribe();
    this.clientsService.updateClientRepresentative({
      client_id: representative.client_id,
      id: representative.id,
      body: representative
    }).subscribe(() => {
      this.invoicePeerService.unlock();
      this.invoicePeerService.refresh();
      this.edited = false;
    });
  }

  ngOnInit() {
    this.clientAndRepresentative$ = this.invoicePeerService
      .getInvoice()
      .pipe(
        switchMap((invoice: Invoice) => {
          if (!invoice || !invoice.client) {
            return EMPTY;
          }
          return this.clientsService.getClientRepresentative({ client_id: invoice.client_id, id: invoice.client_representative_id }).pipe(
            map(representative => ({ client: invoice.client, representative }))
          );
        }),
        catchError(err => {
          console.error('AI: Error loading client and representative data in LE client widget:', err);
          return EMPTY;
        })
      );

    this.invoicePeerService.getState().subscribe((state: string) => {
      if (state === 'locked') {
        this.locked = true;
      } else if (state === 'unlocked') {
        this.locked = false;
      }
    });
  }
}
